import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Lock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const password = fd.get("password");
    const confirm = fd.get("confirm");
    if (password !== confirm) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    if ((password || "").length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Palavra-passe atualizada com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível repor a palavra-passe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 paper-texture" data-testid="reset-page">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center gap-2.5 mb-10 text-primary">
          <BookOpen className="h-9 w-9" />
          <span className="font-heading font-bold text-3xl tracking-tight">EduGrade</span>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl">Definir nova palavra-passe</CardTitle>
            <CardDescription>
              {done
                ? "Tudo pronto! Já pode entrar com a nova palavra-passe."
                : "Escolha uma nova palavra-passe para a sua conta."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="reset-invalid-message">
                  Link inválido ou incompleto. Solicite um novo link de recuperação na página de entrada.
                </div>
                <Button className="w-full" onClick={() => navigate("/auth")} data-testid="reset-goto-login">
                  Ir para entrar
                </Button>
              </div>
            ) : done ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="reset-success-message">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <span>Palavra-passe atualizada. Já pode iniciar sessão.</span>
                </div>
                <Button className="w-full" onClick={() => navigate("/auth")} data-testid="reset-login-btn">
                  Entrar
                </Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-password">Nova palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="reset-password" name="password" type="password" placeholder="Min. 6 caracteres" className="pl-9" minLength={6} required data-testid="reset-password" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm">Confirmar palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="reset-confirm" name="confirm" type="password" placeholder="Repita a palavra-passe" className="pl-9" minLength={6} required data-testid="reset-confirm" />
                  </div>
                </div>
                <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={loading} data-testid="reset-submit">
                  {loading ? "A guardar..." : "Guardar nova palavra-passe"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-6">
          <Link to="/auth" className="text-primary hover:underline" data-testid="reset-back-auth">Voltar à página de entrada</Link>
        </p>
      </div>
    </div>
  );
}
